import { useEffect, useRef, useState } from 'react';
import type { MFAInput } from '#components/atoms/forms/index.ts';
import { useTimeout } from '#hooks/useTimeout.ts';

type InputState = React.ComponentPropsWithoutRef<typeof MFAInput>['state'];

type Props<T> = {
  isSubmitting: boolean;
  isSubmitSuccessful: boolean;
  isSubmitted: boolean;
  setValue: (name: T, value: string) => void;
  setFocus: (name: T) => void;
  name: T;
};

export function useMFAInput<Name>({
  isSubmitting,
  isSubmitSuccessful,
  isSubmitted,
  setValue,
  setFocus,
  name,
}: Props<Name>) {
  const [state, setState] = useState<InputState>('input');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { start: clearInputAfterDelay } = useTimeout(() => {
    setState('input');
    setValue(name, '');
    const event = new Event('input', {
      bubbles: true,
      cancelable: true,
    });
    inputRef.current?.dispatchEvent(event);
    setFocus(name);
  }, 500);

  // Change input state depending on current form state using useEffect to prevent infinite loops
  useEffect(() => {
    if (isSubmitting && state !== 'loading') {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO: Remove this lint error. See LoginMFA.stories.tsx#CanAuthAfterBadCode regression test.
      setState('loading');
    } else if (isSubmitSuccessful && state !== 'success') {
      setState('success');
    } else if (isSubmitted && state === 'loading') {
      // Only trigger error state if we're in loading, otherwise we'll end up in a loop
      setState('error');
      clearInputAfterDelay();
    }
  }, [isSubmitting, isSubmitSuccessful, isSubmitted, state, clearInputAfterDelay]);

  return {
    state,
    inputRef,
  };
}
